import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CButton,
  CForm, CFormInput, CFormSelect, CFormTextarea, CTable, CTableHead, CTableRow,
  CTableHeaderCell, CTableBody, CTableDataCell, CBadge, CSpinner
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilPlus, cilTrash } from '@coreui/icons';
import { toast } from 'react-toastify';

const AudioRulesModal = ({ visible, onClose }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRule, setNewRule] = useState({
    title: '',
    rule_type: 'off_register',
    description: '',
    keywords: '',
    severity: 'red_alert'
  });

  const fetchRules = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/audio-rules/', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRules(res.data.results || []);
    } catch (e) {
      toast.error('Ошибка загрузки правил');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) fetchRules();
  }, [visible]);

  const handleCreateRule = async () => {
    if (!newRule.title || !newRule.description) {
      toast.warn('Заполните название и инструкцию для ИИ');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/audio-rules/', newRule, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Правило добавлено');
      setNewRule({ title: '', rule_type: 'off_register', description: '', keywords: '', severity: 'red_alert' });
      fetchRules();
    } catch (e) {
      toast.error('Ошибка сохранения');
    }
  };

  const handleDeleteRule = async (id) => {
    if (!window.confirm('Удалить правило?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/audio-rules/${id}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRules(prev => prev.filter(r => r.id !== id));
      toast.success('Удалено');
    } catch (e) {}
  };

  return (
    <CModal size="lg" visible={visible} onClose={onClose}>
      <CModalHeader>
        <CModalTitle>🎙️ Настройка правил контроля кассы и скриптов продаж</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <h6 className="fw-bold mb-3">Существующие правила вашей организации:</h6>
        {loading ? <CSpinner /> : (
          <CTable bordered hover responsive size="sm" className="mb-4">
            <CTableHead color="light">
              <CTableRow>
                <CTableHeaderCell>Название</CTableHeaderCell>
                <CTableHeaderCell>Тип</CTableHeaderCell>
                <CTableHeaderCell>Уровень</CTableHeaderCell>
                <CTableHeaderCell>Действие</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {rules.map(r => (
                <CTableRow key={r.id}>
                  <CTableDataCell className="fw-bold">{r.title}</CTableDataCell>
                  <CTableDataCell><CBadge color="info">{r.rule_type}</CBadge></CTableDataCell>
                  <CTableDataCell>
                    <CBadge color={r.severity === 'red_alert' ? 'danger' : 'warning'}>
                      {r.severity === 'red_alert' ? '🚨 Красный Аларм' : '⚠️ Внимание'}
                    </CBadge>
                  </CTableDataCell>
                  <CTableDataCell>
                    <CButton size="sm" color="danger" variant="ghost" onClick={() => handleDeleteRule(r.id)}>
                      <CIcon icon={cilTrash} />
                    </CButton>
                  </CTableDataCell>
                </CTableRow>
              ))}
            </CTableBody>
          </CTable>
        )}

        <div className="bg-light p-3 rounded border">
          <h6 className="fw-bold mb-3 text-primary"><CIcon icon={cilPlus} /> Добавить новое правило / скрипт</h6>
          <CForm>
            <div className="mb-2">
              <label className="form-label small fw-bold">Название правила:</label>
              <CFormInput 
                placeholder="Например: Перевод на Сбербанк / Оплата мимо кассы" 
                value={newRule.title} 
                onChange={e => setNewRule({ ...newRule, title: e.target.value })} 
              />
            </div>
            
            <div className="row g-2 mb-2">
              <div className="col-md-6">
                <label className="form-label small fw-bold">Тип правила:</label>
                <CFormSelect value={newRule.rule_type} onChange={e => setNewRule({ ...newRule, rule_type: e.target.value })}>
                  <option value="off_register">💳 Оплата мимо кассы (Перевод)</option>
                  <option value="sales_script">💐 Скрипт продаж / Стандарты</option>
                  <option value="prohibition">🤬 Запрещенные фразы / Грубость</option>
                  <option value="custom">⚙️ Произвольный сценарий</option>
                </CFormSelect>
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-bold">Уровень реакции:</label>
                <CFormSelect value={newRule.severity} onChange={e => setNewRule({ ...newRule, severity: e.target.value })}>
                  <option value="red_alert">🚨 Красный Аларм (Сразу в TG)</option>
                  <option value="warning">⚠️ Предупреждение (В отчет)</option>
                </CFormSelect>
              </div>
            </div>

            <div className="mb-2">
              <label className="form-label small fw-bold">Ключевые слова/триггеры (через запятую):</label>
              <CFormInput 
                placeholder="перевод, сбер, на карту, номер телефона, скиньте" 
                value={newRule.keywords} 
                onChange={e => setNewRule({ ...newRule, keywords: e.target.value })} 
              />
            </div>

            <div className="mb-3">
              <label className="form-label small fw-bold">Подробная инструкция для ИИ (Как квалифицировать):</label>
              <CFormTextarea 
                rows="3" 
                placeholder="Ищи в тексте ситуации, когда сотрудник дает личный номер для перевода денег или предлагает скидку в обмен на оплату наличными мимо чека." 
                value={newRule.description} 
                onChange={e => setNewRule({ ...newRule, description: e.target.value })} 
              />
            </div>

            <CButton color="success" className="text-white w-100 fw-bold" onClick={handleCreateRule}>
              Сохранить правило
            </CButton>
          </CForm>
        </div>
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" onClick={onClose}>Закрыть</CButton>
      </CModalFooter>
    </CModal>
  );
};

export default AudioRulesModal;